/**
 * MediaRegistry — the host side of the `mediaControl` ActionFlow action
 * (Lane B-7: corpus video-player play / pause / seekPercent / seekRelative /
 * setVolume / setMuted / toggleFullscreen).
 *
 * Block-instance-scoped: a global hook would command the wrong `<video>` when
 * multiple players are mounted. The corpus pairs each mediaControl with a
 * setState driving the reactive UI; this registry holds the live element so the
 * imperative command (play/seek/…) actually reaches the right player. The corpus
 * does NOT carry an element id in params (block-scoped, one player per block), so
 * the registry keys by blockId. The media renderer registers its element on mount
 * (see the `video` element renderer) and unregisters on unmount.
 */

/** The subset of HTMLMediaElement the command applier touches (keeps it test-friendly). */
export interface MediaElementLike {
  play(): Promise<void> | void
  pause(): void
  currentTime: number
  readonly duration: number
  volume: number
  muted: boolean
  requestFullscreen?: () => Promise<void> | void
}

export class MediaRegistry {
  private readonly byBlock = new Map<string, MediaElementLike>()

  /** Register the block's media element. Returns an unregister function. */
  register(blockId: string, element: MediaElementLike): () => void {
    this.byBlock.set(blockId, element)
    return () => {
      if (this.byBlock.get(blockId) === element) this.byBlock.delete(blockId)
    }
  }

  /**
   * Apply a command to the block's media element. Resolution: the block-scoped
   * element first; else, when exactly ONE element is registered page-wide, that
   * one (the common single-player case — race-free even if the dispatching block
   * was mis-resolved). Only a multi-player page with a stale block id misroutes;
   * see app-runtime's currentDispatchBlockNs note. Returns whether an element ran.
   */
  control(blockId: string | null, command: string, args: Record<string, unknown>): boolean {
    let element = blockId ? this.byBlock.get(blockId) : undefined
    if (!element && this.byBlock.size === 1) element = this.byBlock.values().next().value
    if (!element) {
      // eslint-disable-next-line no-console
      console.warn(`[MediaRegistry] no media element for block "${blockId ?? "—"}" (command ${command}; ${this.byBlock.size} registered)`)
      return false
    }
    applyMediaCommand(element, command, args)
    return true
  }

  clear(): void {
    this.byBlock.clear()
  }
}

/** Pure command applier — exported for direct unit testing without the registry. */
export function applyMediaCommand(el: MediaElementLike, command: string, args: Record<string, unknown>): void {
  switch (command) {
    case "play":
      void el.play()
      break
    case "pause":
      el.pause()
      break
    case "seekPercent": {
      const pct = Number(args.percent ?? args.value ?? 0)
      if (Number.isFinite(el.duration) && el.duration > 0) el.currentTime = (pct / 100) * el.duration
      break
    }
    case "seekRelative": {
      const seconds = Number(args.seconds ?? args.offset ?? args.value ?? 0)
      el.currentTime = el.currentTime + seconds
      break
    }
    case "setVolume": {
      const volume = Number(args.volume ?? args.value ?? 0)
      el.volume = Math.max(0, Math.min(1, volume))
      break
    }
    case "setMuted":
      el.muted = Boolean(args.muted ?? args.value ?? false)
      break
    case "toggleFullscreen":
      el.requestFullscreen?.()
      break
    default:
      // eslint-disable-next-line no-console
      console.warn(`[MediaRegistry] unknown command "${command}"`)
  }
}
